import { useMemo } from 'react'
import { keyBy } from 'es-toolkit'
import useI18n from '@/shared/hooks/useI18n'
import FinalizedReportDeclarantsBanner from '@/features/dashboard/pages/finalized-reports/finalized-report-page/components/FinalizedReportDeclarantsBanner'
import MesReportCoverPage from './MesReportCoverPage'
import { MesReportPayload } from '@/shared/hooks/api/integrations'
import FinalizedReportPage from '@/features/dashboard/pages/finalized-reports/finalized-report-page/components/FinalizedReportPage'
import { TableBody } from '@/features/dashboard/pages/finalized-reports/finalized-report-page/components/typography'
import FinalizedReportDetailsTable from '@/features/dashboard/pages/finalized-reports/finalized-report-page/components/FinalizedReportDetailsTable'
import FinalizedReportSpecificEmbeddedEmissionsTable from '@/features/dashboard/pages/finalized-reports/finalized-report-page/components/FinalizedReportSpecificEmbeddedEmissionsTable'
import FinalizedReportCombinedElectricityEmissionsTable from '@/features/dashboard/pages/finalized-reports/finalized-report-page/components/FinalizedReportCombinedElectricityEmissionsTable'
import FinalizedReportElectricityEmissionsTable from '@/features/dashboard/pages/finalized-reports/finalized-report-page/components/FinalizedReportElectricityEmissionsTable'
import { TableCell, TableRow } from '@/common'
import { Installation } from '@/features/installations/api/InstallationModel'
import { ProductionRoute } from '@/features/production-routes/api/ProductionRouteModel'
import { OperatorOrganization } from '@/features/auth/api/AuthModel'

export function DocumentPageContent({ children }: { children: React.ReactNode }) {
  return <div className="flex-grow space-y-4 mt-6">{children}</div>
}

export function DocumentPageSubSection({ children }: { children: React.ReactNode }) {
  return <div className="space-y-2">{children}</div>
}

export type EntityConfig = {
  entityName: string
  street: string
  city: string
  country: string
  email: string
  phone: string
  website: string
}

function Qpd23Element({
  emissionQualifyingParamsNonAluminium,
  numberFormatter
}: {
  emissionQualifyingParamsNonAluminium: { label: string; value: number }[]
  label: string
  numberFormatter: (value: number) => string
}) {
  const tableElements = emissionQualifyingParamsNonAluminium.filter(element => element.value > 0)
  const totalPercentage = numberFormatter(
    tableElements.reduce((acc, element) => acc + element.value, 0)
  )
  return (
    <div className="space-y-4">
      <p>
        - For purposes of reporting QPD23 for a CBAM report, {totalPercentage}% of materials other
        than aluminium were used
      </p>

      <table className="w-full text-left border">
        <thead className="bg-blue-300 text-text-secondary text-sm">
          <tr>
            <th className="py-2 px-4">Material</th>
            <th className="py-2 px-4">Percentage</th>
          </tr>
        </thead>
        <TableBody>
          {tableElements.map(param => (
            <tr key={param.label} className="border-t">
              <td className="py-2 px-4 font-bold">{param.label}</td>
              <td className="py-2 px-4">{numberFormatter(param.value)}%</td>
            </tr>
          ))}
        </TableBody>
        <tfoot>
          <TableRow>
            <TableCell className="font-bold">Total</TableCell>
            <TableCell className="font-bold">{totalPercentage}%</TableCell>
          </TableRow>
        </tfoot>
      </table>
    </div>
  )
}

export function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="font-bold text-lg">{children}</h2>
}

export function SubSectionTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="text-text-secondary">{children}</h3>
}

const MES_REPORT_CONFIG: Record<string, EntityConfig> = {
  artem: {
    entityName: 'ARTEM Technologie AG',
    street: 'Sinserstraße 67',
    city: '6330 Cham Zug',
    country: 'Switzerland',
    email: 'info@artem.earth',
    phone: '+41 41 560 55 00',
    website: 'https://artem.earth'
  }
}

export default function MesReportEmbed({
  data: {
    electricityEmissionFactor,
    emissionQualifyingParams,
    isFullCalendarYear,
    methodAggregatedEmissions
  },
  isPreview,
  installation,
  productionRoute,
  organization,
  startDate,
  endDate
}: {
  data: MesReportPayload
  isPreview?: boolean
  installation: Installation
  productionRoute: ProductionRoute
  organization: OperatorOrganization
  startDate: string
  endDate: string
}) {
  const finalizedAt = new Date().toISOString()
  const { t, dateLongFormatterGB, numberFormatter4Decimals } = useI18n()

  const aggregatedElectricity = {
    sources: [
      {
        type: 'GRID',
        totalConsumptionShare: 1,
        weightedAvgEmissionFactor: Number(electricityEmissionFactor)
      }
    ]
  }

  const installationCountryName = t(`countriesByIsoAlpha2:${installation.country}`)
  const organizationCountryName = t(`countriesByIsoAlpha2:${organization.country}`)
  const unlocodeWithCountry = installation.unlocode
    ? `${installation.country.toUpperCase()} ${installation.unlocode}`
    : null

  const operatorName = organization.name
  const installationName = installation.name

  const sourcesByType = keyBy(aggregatedElectricity.sources, source => source.type)
  const isUsingDirectTechnicalLink = !!sourcesByType['DIRECT_TECHNICAL_LINK']
  const isUsingPPA = !!sourcesByType['PPA']

  const startDateFormatted = dateLongFormatterGB(new Date(startDate))
  const endDateFormatted = dateLongFormatterGB(new Date(endDate))
  const creationDateFormatted = dateLongFormatterGB(new Date())
  const finalizedDateFormatted = useMemo(() => {
    if (!finalizedAt) {
      return 'TBA'
    }

    const date = new Date(finalizedAt)

    return dateLongFormatterGB(date)
  }, [finalizedAt, dateLongFormatterGB])

  const installationDetails = [
    {
      title: 'Installation name',
      value: installationName
    },
    {
      title: 'Street',
      value: installation.street
    },
    {
      title: 'Additional Address Information',
      value: installation.street2
    },
    {
      title: 'Postal code',
      value: installation.postCode
    },
    {
      title: 'City',
      value: installation.city
    },
    {
      title: 'Country',
      value: installationCountryName
    },
    {
      title: 'UN/LOCODE',
      value: unlocodeWithCountry
    },
    {
      title: 'Longitude',
      value: installation.longitude
    },
    {
      title: 'Latitude',
      value: installation.latitude
    }
  ]

  const organizationDetails = [
    {
      title: 'Street',
      value: organization.street
    },
    {
      title: 'Street number',
      value: organization.streetNumber
    },
    {
      title: 'Additional Address Information',
      value: organization.street2
    },
    {
      title: 'Postal code',
      value: organization.postCode
    },
    {
      title: 'City',
      value: organization.city
    },
    {
      title: 'PO Box',
      value: organization.poBox
    },
    {
      title: 'Country',
      value: organizationCountryName
    }
  ]

  const contactDetails = [
    {
      title: 'Contact person name',
      value: organization.contactName
    },
    {
      title: 'Phone number',
      value: organization.contactPhoneNumber
    },
    {
      title: 'Email address',
      value: organization.contactEmail
    }
  ]

  const electricityCombined = aggregatedElectricity.sources.reduce(
    (acc, source) => {
      return {
        totalConsumptionShare: acc.totalConsumptionShare + source.totalConsumptionShare,
        weightedAvgEmissionFactor:
          acc.weightedAvgEmissionFactor +
          source.weightedAvgEmissionFactor * source.totalConsumptionShare
      }
    },
    { totalConsumptionShare: 0, weightedAvgEmissionFactor: 0 }
  )

  const cnCodes = productionRoute?.productionMethods.flatMap(method =>
    method.cnCodes.map(cnCode => ({
      methodCode: method.code,
      cnCode: cnCode
    }))
  )

  const emissionQualifyingParamsArray = Object.keys(emissionQualifyingParams) as Array<
    keyof typeof emissionQualifyingParams
  >

  const emissionQualifyingParamsNonAluminium = useMemo(
    () =>
      emissionQualifyingParamsArray
        .filter(param => param === 'QPD23')
        .map(param => emissionQualifyingParams[param]?.elements)
        .flatMap(elements =>
          elements
            ? elements.map(element => ({
                label: element.name,
                value: element.value
              }))
            : []
        ),
    [emissionQualifyingParams, emissionQualifyingParamsArray]
  )

  const formattedQpd21 = numberFormatter4Decimals(emissionQualifyingParams.QPD21?.value ?? 0)
  const qpd21 = (
    <>
      - {formattedQpd21} t of scrap was used to make 1 tonne of the product. This assessment
      excludes scrap used to produce secondary aluminium precursors.
    </>
  )
  const qpd22 = (
    <>
      - All scrap utilised within the facility for the production process is pre-consumer scrap,
      accounting for 100% of the total scrap input. This assessment excludes scrap used to produce
      secondary aluminium precursors.
    </>
  )
  const qpd23 = (
    <Qpd23Element
      emissionQualifyingParamsNonAluminium={emissionQualifyingParamsNonAluminium}
      label={t('emissionQualifyingParams:fields.QPD23.reportText')}
      numberFormatter={numberFormatter4Decimals}
    />
  )

  const footNote = isFullCalendarYear
    ? null
    : 'Please note that the reporting period of this report is less than a full calendar year. Additional confirmation may be required to verify that this period is permitted pursuant to the CBAM Regulation.'

  return (
    <div className="max-w-4xl mx-auto text-sm mb-10">
      {/* Cover Page */}
      <MesReportCoverPage
        operatorName={operatorName}
        cnCodes={cnCodes ?? []}
        startDate={startDateFormatted}
        endDate={endDateFormatted}
        creationDate={creationDateFormatted}
        finalizedDate={finalizedDateFormatted}
        productionRouteName={productionRoute.name}
        entityConfig={MES_REPORT_CONFIG.artem}
      />

      {/* Page 1: Intro + Installation & Production Route */}
      <FinalizedReportPage
        isPreview={isPreview}
        pageNumber="1"
        totalPage="5"
        entityConfig={MES_REPORT_CONFIG.artem}
        footNote={footNote}
      >
        <DocumentPageContent>
          <section className="space-y-4">
            <SectionTitle>1. Introduction</SectionTitle>
            <p>
              This Emissions Report has been generated by {operatorName} ("Operator") for the
              purpose of supporting a declaration made by a Declarant under the Carbon Border
              Adjustment Mechanism ("CBAM") established by Regulation (EU) 2023/956 of 10 May 2023
              and Implementing Regulation (EU) 2023/1773 of 17 August 2023 ("CBAM Regulations").
            </p>
            <p>
              This Emissions Report has been generated by the Operator using ARTEM Operators, an
              easy-to-use solution that simplifies regulatory compliance. All data has been
              normalised with the intention that a Declarant can enter the data from this Emissions
              Report directly into the European Union Declarant Portal without any further
              calculations.
            </p>
            <p>
              Where the Operator has made assumptions as to its activity data, emission factors,
              calculation factors, and embedded emissions of its purchased precursors, these
              assumptions are explicitly set out in this Report.
            </p>
            <p>
              This Report has been finalised by the Operator and is accurate as of{' '}
              {finalizedDateFormatted}. At the time of finalisation of this Report, and to the best
              of the Operator’s knowledge, this Report does not contain any errors, omissions, or
              material misstatements.
            </p>
          </section>
          <section className="space-y-4">
            <header className="space-y-2">
              <SectionTitle>2. Installation & Production Route</SectionTitle>
            </header>

            <DocumentPageSubSection>
              <SubSectionTitle>a. Address</SubSectionTitle>
              <p>
                The installation which produced the relevant goods for this report has the following
                details:
              </p>

              <FinalizedReportDetailsTable items={installationDetails} />
            </DocumentPageSubSection>

            <DocumentPageSubSection>
              <SubSectionTitle>b. Production Route</SubSectionTitle>
              <p>
                This Emissions Report pertains to the following{' '}
                {t(`common:aggregatedGoods.${productionRoute.aggregatedGood}`)} produced using the{' '}
                {productionRoute.name} production route (“Goods”):
              </p>
              <p>The Goods have the following CN codes:</p>
              <ul className="list-disc pl-5 mb-4 font-semibold">
                {cnCodes?.map(method => (
                  <li key={method.methodCode}>{method.cnCode}</li>
                ))}
              </ul>
              <p>
                The Emissions Data and Additional Supporting Information set out below are
                applicable to all of the Goods. For the purposes of a Declarant entering data into
                the EU Portal, the data below has been normalised per ton of Good and each figure
                can be entered directly into the EU Portal without any further calculations.
              </p>
            </DocumentPageSubSection>
          </section>
        </DocumentPageContent>
      </FinalizedReportPage>

      {/* Page 2: Emissions */}
      <FinalizedReportPage
        isPreview={isPreview}
        pageNumber="2"
        totalPage="5"
        entityConfig={MES_REPORT_CONFIG.artem}
        footNote={footNote}
      >
        <DocumentPageContent>
          <SectionTitle>3. Emissions</SectionTitle>

          {/* Section a */}
          <DocumentPageSubSection>
            <SubSectionTitle>a. Specific Embedded Emissions</SubSectionTitle>
            <p>
              The Operator has calculated the Specific Embedded Emissions of the goods as follows:
            </p>
            <FinalizedReportSpecificEmbeddedEmissionsTable
              methodAggregatedEmissions={methodAggregatedEmissions[0]}
            />

            <p className="text-xs italic text-text-secondary mb-8">
              Emissions data for this production route has been captured through ARTEM Operators,
              and the Operator has supplied data regarding the traceability of source inputs, with
              the reported specific direct and indirect emissions encompassing both precursor and
              on-site emissions in line with CBAM requirements.
            </p>
          </DocumentPageSubSection>

          {/* Section b */}
          <DocumentPageSubSection>
            <SubSectionTitle>b. Specific Indirect Embedded Emissions</SubSectionTitle>
            <p>The Specific indirect emissions for the goods has the following information:</p>
            <FinalizedReportCombinedElectricityEmissionsTable
              showOnlyCombinedEmissions
              methodAggregatedEmissions={methodAggregatedEmissions[0]}
            />
            <p>
              The Operator's installation level electricity is based on the following combination of
              electricity sources:
            </p>
            <FinalizedReportElectricityEmissionsTable
              aggregatedElectricity={aggregatedElectricity}
              electricityCombined={electricityCombined}
            />

            <div className="space-y-4">
              <p>
                Electricity generated from the grid has been calculated using the default emission
                factor for {installationCountryName} according to latest updated IEA figure.
              </p>
              {isUsingDirectTechnicalLink && (
                <p>
                  The Operator has supplied the following information regarding the Direct Technical
                  Link at the Installation:
                  <br />
                  {
                    productionRoute?.electricityProductionMethods.find(
                      pm => pm.type === 'DIRECT_TECHNICAL_LINK'
                    )?.info
                  }
                </p>
              )}
              {isUsingPPA && (
                <p>
                  The Operator has supplied the following information regarding the Bi-Lateral Power
                  Purchase Agreement:
                  {
                    productionRoute?.electricityProductionMethods.find(pm => pm.type === 'PPA')
                      ?.info
                  }
                </p>
              )}
              <p>
                The Declarant can input the following text in the box “Other source Indication” to
                report the indirect emissions on the EU Portal:
              </p>
              <div className="space-y-2 bg-gray-100 p-2 italic">
                <p>
                  Precursor emission factors were calculated using country-specific grid emission
                  factors provided by the EU Commission. Electricity consumption was derived from
                  the precursor-specific indirect embedded emissions and the corresponding emission
                  factors. The Operator has calculated the weighted average emission factor at the
                  installation level according to the installation’s energy mix of electricity from
                  the grid. For grid electricity the IEA emission factor has been used.
                </p>
              </div>
            </div>
          </DocumentPageSubSection>
        </DocumentPageContent>
      </FinalizedReportPage>

      {/* Page 3: Additional supporting information */}
      <FinalizedReportPage
        pageNumber="3"
        totalPage="5"
        entityConfig={MES_REPORT_CONFIG.artem}
        footNote={footNote}
      >
        <DocumentPageContent>
          <SectionTitle>4. Additional supporting information</SectionTitle>

          {/* Section a */}
          <div className="space-y-2">
            <SubSectionTitle>a. Qualifying Parameters</SubSectionTitle>
            <p>
              The Goods have the following sector-specific information which are required for the
              purposes of reporting:
            </p>
            <ul className="space-y-1.5">
              <li>{qpd21}</li>
              <li>{qpd22}</li>
              <li>{qpd23}</li>
            </ul>
          </div>
        </DocumentPageContent>
      </FinalizedReportPage>

      {/* Page 4: Additional supporting information */}
      <FinalizedReportPage
        isPreview={isPreview}
        pageNumber="4"
        totalPage="5"
        entityConfig={MES_REPORT_CONFIG.artem}
        footNote={footNote}
      >
        <DocumentPageContent>
          {/* Section b */}
          <DocumentPageSubSection>
            <SubSectionTitle>b. Monitoring methodology and data quality</SubSectionTitle>
            <p>
              The Operator has determined its emissions on the basis of activity data obtained by
              the Operator’s measurement systems and calculation factors from standard values.
            </p>
            <p>
              The Operator uses the following data sources to calculate the activity data required
              for performing emission calculations. The source of data is selected according to the
              best available data source (as required by the CBAM Regulations) and is selected in
              accordance with the following priority (i) Metered/Measured Data, and (ii)
              Invoiced/Billed Data:
            </p>
            <table className="w-full text-left border border-gray-300">
              <thead className="bg-blue-300 text-text-secondary text-sm">
                <tr>
                  <th className="py-2 px-4">Data Type</th>
                  <th className="py-2 px-4">Priority</th>
                  <th className="py-2 px-4">Data Types</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t">
                  <td className="py-2 px-4">Metered/Measured Data</td>
                  <td className="py-2 px-4">Highest Priority</td>
                  <td className="py-2 px-4">
                    <p>Direct fuel consumption (e.g., gas/diesel meters, tank logs).</p>
                    <p>On-site electricity consumption (meters/sub-metering).</p>
                  </td>
                </tr>
                <tr className="border-t">
                  <td className="py-2 px-4">Invoiced/Billed Data</td>
                  <td className="py-2 px-4">Second Priority</td>
                  <td className="py-2 px-4">
                    <p>Verified fuel purchase invoices (volume/weight).</p>
                    <p>Electricity bills (kWh) from utility providers.</p>
                  </td>
                </tr>
              </tbody>
            </table>
            <p>
              The Operator maintains digital/physical records of fuel/electricity invoices (with
              dates, volumes, supplier details) and meter readings (time-stamped, with calibration
              records).If data is missing or unverifiable, the Operator uses conservative estimates
              (upper-bound values) and documents assumptions.
            </p>
            <p>
              The Operator is currently in the process of implementing an electronic data log via
              ARTEM Operators to input data sources and perform the calculation steps.
            </p>

            <p>
              The Operator also intends to conduct annual third-party pre-verification by ARTEM.
            </p>

            <p>The Operator has reasonable assurance as to its data quality on the basis that:</p>

            <p>
              (1) the data forming the basis of this Report is derived from the relevant extracts of
              the Operator’s Manufacturing Execution System.
            </p>

            <p>
              (2) a representative of the Operator has reviewed this report and is satisfied that it
              gives a fair and reasonable calculation of the emissions within the maximum margin of
              error permitted by the Regulations.
            </p>
          </DocumentPageSubSection>

          {/* Section c */}
          <DocumentPageSubSection>
            <SubSectionTitle>c. Activity level validation</SubSectionTitle>
            <p>
              In conjunction with developing its Monitoring Reporting and Verification
              Documentation, the Operator intends to make improvements to its monitoring processes,
              and is considering such measures as the phasing in of IoT sensors/automated monitoring
              for real-time data, and improving the training of staff on data collection protocols.
            </p>
            <p>
              The operator anticipates that its production protocols will conform to European
              Standard EN 45557 throughout future reporting periods, providing a separate accounting
              of precursors, scrap, and other material inputs, as well as the final product,
              internal conversions, waste, and returns to stockpile.
            </p>
          </DocumentPageSubSection>

          {/* Section d */}
          <DocumentPageSubSection>
            <SubSectionTitle>d. Carbon Price paid</SubSectionTitle>
            <p>
              There has not been any payment of a carbon price in the country in which the goods
              originate which can be used to offset the CBAM liability.
            </p>
          </DocumentPageSubSection>
        </DocumentPageContent>
      </FinalizedReportPage>

      {/* Page 5: Organisation contact information */}
      <FinalizedReportPage
        isPreview={isPreview}
        pageNumber="5"
        totalPage="5"
        entityConfig={MES_REPORT_CONFIG.artem}
        footNote={footNote}
      >
        <DocumentPageContent>
          <section className="space-y-2">
            <header className="space-y-2">
              <SectionTitle>5. Organisation contact information</SectionTitle>
              <p className="mt-2 mb-4">Organisation contact information and contact person:</p>
            </header>
            <div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <SubSectionTitle>a. Organisation name</SubSectionTitle>
                  <FinalizedReportDetailsTable
                    items={[
                      {
                        title: 'Organisation name',
                        value: organization.name
                      }
                    ]}
                  />
                </div>
                <div className="space-y-2">
                  <SubSectionTitle>b. Operator address</SubSectionTitle>
                  <FinalizedReportDetailsTable items={organizationDetails} />
                </div>
                <div className="space-y-2">
                  <SubSectionTitle>c. Operator contact details</SubSectionTitle>
                  <FinalizedReportDetailsTable items={contactDetails} />
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-2">
            <SectionTitle>6. About ARTEM</SectionTitle>
            <p>
              ARTEM Operators is a CBAM compliance solution that assists operators around the world
              to comply with the requirements of CBAM, and automate data collection to reduce the
              likelihood of manual input errors.
            </p>
            <p>
              For up-to-date forecasts of your Operator’s emission profile for this year,
              information regarding your Operator’s readiness for verification, and insights into
              the quality of your emissions data you can sign up to ARTEM Declarants!
            </p>
            <p>
              ARTEM Declarants continuously updates emissions forecasts based on latest operator
              data, giving you visibility and confidence regarding your CBAM compliance.
            </p>
          </section>
          <FinalizedReportDeclarantsBanner />
        </DocumentPageContent>
        {/* do not remove the below line as it is needed for PDF */}
        <div data-artem="render-completed"></div>
      </FinalizedReportPage>
    </div>
  )
}
